import { Scales } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { DefinitionBox } from '../../components/onboarding/DefinitionBox';
import { SelectionCountBadge } from '../../components/onboarding/SelectionCountBadge';
import { SelectionGroup } from '../../components/onboarding/SelectionGroup';
import { WizardStepHeader } from '../../components/wizard';
import { useOnboardingFooter } from '../../hooks/useOnboardingFooter';
import { authAPI } from '../../data/auth';
import { invalidateGuidedFlow } from '../../features/guidedFlow/queries';
import { DEBT_GROUPS, ONBOARDING_DEFINITIONS } from '../../lib/onboardingCatalogs';
import pt from '../../locales/pt';
import { useAuth } from '../../store/AuthContext';

const NONE_KEY = 'none';
export const DEBT_DRAFT_KEY = 'evplanner.onboarding.debtKeys';

export function readDebtDraft() {
  try {
    const stored = sessionStorage.getItem(DEBT_DRAFT_KEY);
    const parsed = stored ? JSON.parse(stored) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch (_err) {
    return null;
  }
}

export function writeDebtDraft(keys) {
  try {
    sessionStorage.setItem(DEBT_DRAFT_KEY, JSON.stringify(keys || []));
  } catch (_err) {
    // ignore quota / private mode
  }
}

/** Initial chips: session draft, else "Nenhuma" if profile says no debts. */
export function resolveInitialDebtKeys(profile, draft = readDebtDraft()) {
  if (Array.isArray(draft) && draft.length > 0) {
    return draft;
  }
  if (profile?.has_active_debts === false) {
    return [NONE_KEY];
  }
  return [];
}

export function OnboardingDebtsSelect() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { profile, refreshMe } = useAuth();
  const [selected, setSelected] = useState(() => resolveInitialDebtKeys(profile));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // If draft empty but profile refreshed after mount with has_active_debts=false
    if (selected.length > 0) return;
    if (profile?.has_active_debts === false) {
      setSelected([NONE_KEY]);
      writeDebtDraft([NONE_KEY]);
    }
  }, [profile?.has_active_debts, selected.length]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const debtKeys = useMemo(
    () => selected.filter((k) => k !== NONE_KEY),
    [selected],
  );

  const toggle = (key) => {
    setSelected((current) => {
      let next;
      if (key === NONE_KEY) {
        next = current.includes(NONE_KEY) ? [] : [NONE_KEY];
      } else {
        const withoutNone = current.filter((k) => k !== NONE_KEY);
        next = withoutNone.includes(key)
          ? withoutNone.filter((k) => k !== key)
          : [...withoutNone, key];
      }
      writeDebtDraft(next);
      return next;
    });
  };

  const finish = useCallback(
    async (hasActiveDebts) => {
      setSaving(true);
      await authAPI.patchProfile({ has_active_debts: hasActiveDebts });
      await authAPI.saveOnboardingStep('ready');
      await refreshMe();
      invalidateGuidedFlow(qc);
      setSaving(false);
      navigate('/onboarding/ready');
    },
    [navigate, qc, refreshMe],
  );

  const onContinue = useCallback(() => {
    if (selected.includes(NONE_KEY) || debtKeys.length === 0) {
      writeDebtDraft(selected.includes(NONE_KEY) ? [NONE_KEY] : []);
      return finish(false);
    }
    writeDebtDraft(selected);
    return finish(true);
  }, [debtKeys.length, finish, selected]);

  const onSkip = useCallback(() => {
    writeDebtDraft([NONE_KEY]);
    return finish(false);
  }, [finish]);

  useOnboardingFooter({
    primaryLabel: pt.onboarding.common.continue,
    onPrimary: onContinue,
    onSkip,
    skipLabel: pt.onboarding.common.skipNoDebts,
    loading: saving,
  });

  return (
    <div className="flex flex-col gap-space-6">
      <div className="flex items-start justify-between gap-3">
        <WizardStepHeader
          eyebrow={pt.onboarding.debts.eyebrow}
          title={pt.onboarding.debts.title}
          subtitle={pt.onboarding.debts.subtitle}
          className="flex-1"
        />
        <SelectionCountBadge count={debtKeys.length} className="mt-7 shrink-0" />
      </div>

      <DefinitionBox icon={Scales}>{ONBOARDING_DEFINITIONS.debts}</DefinitionBox>

      {DEBT_GROUPS.map((group) => (
        <SelectionGroup
          key={group.id}
          name={group.name}
          pctLabel={group.pctLabel}
          description={group.description}
          dotClass={group.dotClass}
          tint={group.tint}
          items={group.items}
          selectedKeys={selectedSet}
          getKey={(item) => item.key}
          onToggle={(key) => toggle(key)}
        />
      ))}
    </div>
  );
}

/** @deprecated use OnboardingDebtsSelect */
export { OnboardingDebtsSelect as OnboardingDebtsCheck };
